import React, { useState } from 'react';
import { Tree } from '../types/Tree';
import QuestionInterface from './QuestionInterface';
import ImageModal from './ImageModal';
import './TreeDetail.css';

interface TreeDetailProps {
  tree: Tree;
  onBack: () => void;
}

const TreeDetail: React.FC<TreeDetailProps> = ({ tree, onBack }) => {
  const [showQuestions, setShowQuestions] = useState(false);
  const [modalImage, setModalImage] = useState<{
    url: string;
    alt: string;
    title?: string;
    description?: string;
  } | null>(null);

  const openImageModal = (url: string, alt: string, title?: string, description?: string) => {
    setModalImage({ url, alt, title, description });
  };

  const closeImageModal = () => {
    setModalImage(null);
  };

  return (
    <div className="tree-detail">
      <button className="back-button" onClick={onBack}>
        ← Back to Trees
      </button>
      
      <div className="tree-detail-header">
        <div className="tree-images-container">
          <div className="tree-detail-image-wrapper">
            <img 
              src={tree.imageUrl} 
              alt={tree.name}
              className="tree-detail-image clickable-image"
              onClick={() => openImageModal(
                tree.imageUrl, 
                tree.name,
                tree.name,
                `A beautiful ${tree.name} (${tree.scientificName}) found in California parks.`
              )}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent && !parent.querySelector('.tree-detail-placeholder')) {
                  const placeholder = document.createElement('div');
                  placeholder.className = 'tree-detail-placeholder';
                  placeholder.textContent = '🌳';
                  parent.appendChild(placeholder);
                }
              }}
            />
            <span className="image-label">Tree (Click to enlarge)</span>
          </div>
          {tree.leafImageUrl && (
            <div className="tree-detail-image-wrapper">
              <img 
                src={tree.leafImageUrl} 
                alt={`${tree.name} leaves`}
                className="tree-detail-image clickable-image"
                onClick={() => openImageModal(
                  tree.leafImageUrl!, 
                  `${tree.name} leaves`,
                  `${tree.name} Leaves`,
                  `Close-up view of ${tree.name} leaves. ${tree.characteristics.leaves}`
                )}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent && !parent.querySelector('.tree-detail-placeholder')) {
                    const placeholder = document.createElement('div');
                    placeholder.className = 'tree-detail-placeholder';
                    placeholder.textContent = '🍃';
                    parent.appendChild(placeholder);
                  }
                }}
              />
              <span className="image-label">Leaves (Click to enlarge)</span>
            </div>
          )}
        </div>
        <div className="tree-title-info">
          <h1>{tree.name}</h1>
          <p className="scientific-name">{tree.scientificName}</p>
        </div>
      </div>

      <div className="tree-content">
        <section className="tree-section">
          <h2>About This Tree</h2>
          <p>{tree.description}</p>
        </section>

        <section className="tree-section">
          <h2>What Does It Look Like?</h2>
          <div className="characteristics-grid">
            <div className="characteristic-item">
              <strong>Height:</strong> {tree.characteristics.height}
            </div>
            <div className="characteristic-item">
              <strong>Leaves:</strong> {tree.characteristics.leaves}
            </div>
            <div className="characteristic-item">
              <strong>Bark:</strong> {tree.characteristics.bark}
            </div>
            {tree.characteristics.smell && (
              <div className="characteristic-item">
                <strong>Smell:</strong> {tree.characteristics.smell}
              </div>
            )}
          </div>
        </section>

        <section className="tree-section">
          <h2>How Does It Help Nature?</h2>
          <ul className="ecosystem-list">
            {tree.ecosystemValue.map((value, index) => (
              <li key={index}>{value}</li>
            ))}
          </ul>
        </section>

        <section className="tree-section">
          <h2>What Animals Live Here?</h2>
          <div className="wildlife-grid">
            <div className="wildlife-group">
              <h3>🐦 Birds</h3>
              <div className="wildlife-items">
                {tree.wildlife.birds.map((bird, index) => (
                  <div key={index} className="wildlife-item">
                    <div className="wildlife-image-wrapper">
                      <img 
                        src={bird.imageUrl} 
                        alt={bird.name}
                        className="wildlife-image clickable-image"
                        onClick={() => openImageModal(
                          bird.imageUrl,
                          bird.name,
                          bird.name,
                          `A ${bird.name} that commonly lives in and around ${tree.name} trees. These birds are often seen in California parks where these trees grow.`
                        )}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent && !parent.querySelector('.wildlife-placeholder')) {
                            const placeholder = document.createElement('div');
                            placeholder.className = 'wildlife-placeholder';
                            placeholder.textContent = '🐦';
                            parent.appendChild(placeholder);
                          }
                        }}
                      />
                    </div>
                    <span className="wildlife-name">{bird.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="wildlife-group">
              <h3>🐛 Insects</h3>
              <div className="wildlife-items">
                {tree.wildlife.insects.map((insect, index) => (
                  <div key={index} className="wildlife-item">
                    <div className="wildlife-image-wrapper">
                      <img 
                        src={insect.imageUrl} 
                        alt={insect.name}
                        className="wildlife-image clickable-image"
                        onClick={() => openImageModal(
                          insect.imageUrl,
                          insect.name,
                          insect.name,
                          `A ${insect.name} that lives on or around ${tree.name} trees. These insects play an important role in the tree's ecosystem.`
                        )}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent && !parent.querySelector('.wildlife-placeholder')) {
                            const placeholder = document.createElement('div');
                            placeholder.className = 'wildlife-placeholder';
                            placeholder.textContent = '🐛';
                            parent.appendChild(placeholder);
                          }
                        }}
                      />
                    </div>
                    <span className="wildlife-name">{insect.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="tree-section">
          <h2>Cool Facts! 🤓</h2>
          <ul className="facts-list">
            {tree.interestingFacts.map((fact, index) => (
              <li key={index}>{fact}</li>
            ))}
          </ul>
        </section>

        <div className="question-prompt">
          <h2>Want to Learn More?</h2>
          <p>Ask me questions about this tree! I love curious kids! 🌟</p>
          <button 
            className="ask-questions-btn"
            onClick={() => setShowQuestions(!showQuestions)}
          >
            {showQuestions ? 'Hide Questions' : 'Ask Questions!'}
          </button>
        </div>

        {showQuestions && (
          <QuestionInterface tree={tree} />
        )}
      </div>

      <ImageModal
        isOpen={modalImage !== null}
        imageUrl={modalImage?.url || ''}
        imageAlt={modalImage?.alt || ''}
        title={modalImage?.title}
        description={modalImage?.description}
        onClose={closeImageModal}
      />
    </div>
  );
};

export default TreeDetail;